import { Box, MenuItem, Select } from "@mui/material";
import { isValid } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { convertDateToYYYYMMDDString } from "../../../../../app/common";
import { DEFAULT_DRAG_HANDLE_SELECTOR } from "../../../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { CustomButton } from "../../../../../common/components/functional/customButton/CustomButton";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import {
  Entry,
  Leave,
  LeaveEntity,
  LeaveEntityDateRange,
  LeaveMap,
} from "../../../entriesAPI";
import { fetchAbsencesById } from "../../../entriesSlice";
import { filterAbsenceTypes } from "../../Shared/functions";
import { SICK_LEAVE_TYPES } from "../Absences";
import {
  getAbsenceHistoryFromDate,
  getAbsenceWithContinuation,
} from "../helpers";
import LeaveAccumulationInfo from "./LeaveAccumulationInfo";
import ToGeneralLeave from "./ToGeneralLeave/ToGeneralLeave";
import ToSickLeave from "./ToSickLeave/ToSickLeave";
import useLeaveAccumulation from "./useLeaveAccumulation";

interface Props {
  entry: Entry | null;
  setSelectedEntry: (val: Entry) => void;
  closeConvertModal: (val?: boolean) => void;
  leaveEntity: LeaveEntity;
  updateLeaveEntity: (
    key: string,
    value: string | LeaveEntityDateRange | Leave | null,
  ) => void;
  clearLeaveEntity: () => void;
  setLeaveEntity: (val: LeaveEntity) => void;
  translatedEntryTypes: { [key: string]: string };
  talentId?: string;
  createNewAbsence?: boolean;
}

const ConvertToAbsence = ({
  entry,
  setSelectedEntry,
  closeConvertModal,
  leaveEntity,
  updateLeaveEntity,
  clearLeaveEntity,
  setLeaveEntity,
  translatedEntryTypes,
  talentId,
  createNewAbsence,
}: Props) => {
  const [continuePreviousLeave, setContinuePreviousLeave] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState<{ [code: string]: boolean }>(
    {},
  );

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const foundTalentId = entry?.talentId || talentId;

  const {
    rows: accumulationRows,
    unit: accumulationUnit,
    labelKey: accumulationLabelKey,
    loading: accumulationLoading,
  } = useLeaveAccumulation(foundTalentId, leaveEntity.type);

  // Get assignment from state
  const assignment = useAppSelector((state) => state.talents.assignment);

  // Get absence types based on assignment or use default absence types
  const absenceTypes = useMemo(() => {
    // List of prioritized absence types that appear on top of list
    const priorityTypes: string[] = [
      "LEVELLING_LEAVE",
      "SICK_LEAVE",
      "SICK_LEAVE_SELF_REPORTED",
      "SICK_LEAVE_OCCUPATIONAL_ACCIDENT",
      "ANNUAL_HOLIDAY",
    ];

    const availableAbsenceTypes = filterAbsenceTypes(
      translatedEntryTypes,
      createNewAbsence ? [] : assignment?.details?.availableEntryTypes,
      priorityTypes,
      [],
      entry?.isShift,
    );

    return availableAbsenceTypes
      .map((type) => ({
        value: type,
        name: translatedEntryTypes[type],
      }))
      .filter((type) => type.name);
  }, [assignment, translatedEntryTypes, createNewAbsence, entry?.isShift]);

  useEffect(() => {
    // Helper function to adjust end date if it ends exactly at midnight (00:00:00)
    const adjustEndDateIfMidnight = (date: Date): Date => {
      if (
        date.getHours() === 0 &&
        date.getMinutes() === 0 &&
        date.getSeconds() === 0
      ) {
        // Adjust the date to be the previous day at 23:59:59
        date.setDate(date.getDate() - 1);
        date.setHours(23, 59, 59, 999);
      }
      return date;
    };
    // During initial render (and if both fields are cleared), set date range to entry start and end dates
    !leaveEntity?.dateRange?.startDate &&
      !leaveEntity?.dateRange?.endDate &&
      entry?.start &&
      entry?.end &&
      updateLeaveEntity("dateRange", {
        startDate: convertDateToYYYYMMDDString(new Date(entry.start), "-"),
        // Adjust end date if it ends exactly at midnight
        endDate: convertDateToYYYYMMDDString(
          adjustEndDateIfMidnight(new Date(entry.end)),
          "-",
        ),
      });

    if (
      foundTalentId &&
      leaveEntity?.dateRange?.startDate &&
      leaveEntity?.dateRange?.endDate
    ) {
      setIsLoading(true);

      // If endDate is not valid, don't try to fetch
      if (!isValid(new Date(leaveEntity?.dateRange?.endDate))) {
        setIsLoading(false);
        return;
      }

      // Fetch absences for talent
      dispatch(
        fetchAbsencesById({
          talentId: foundTalentId,
          type: leaveEntity.type,
          from: getAbsenceHistoryFromDate(leaveEntity.dateRange.startDate),
          to: leaveEntity?.dateRange?.endDate,
        }),
      ).then((action: any) => {
        const responseData: LeaveMap = action.payload;
        const newEnt = getAbsenceWithContinuation(responseData, leaveEntity);
        setLeaveEntity(newEnt);

        // If there are no previous absences, disable continue previous
        responseData.order.length === 0
          ? setContinuePreviousLeave(false)
          : setContinuePreviousLeave(true);

        setIsLoading(false);
      });
    }
    // Prevent additional re-renders and api calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveEntity.dateRange, leaveEntity.talentId, leaveEntity.type]);

  const handleAbort = () => {
    clearLeaveEntity();
    closeConvertModal();
  };

  const handleContinue = () => {
    if (leaveEntity?.dateRange?.startDate && leaveEntity?.dateRange?.endDate) {
      // When selected entry type is changed, it causes modal to change into chosen type of modal (sickleave, vacation etc.)
      setSelectedEntry({
        ...entry,
        type: leaveEntity.type,
      } as Entry);

      // If user has decided not to continue previous leave, but it's selected for some reason, clear it from leaveEntity
      if (!continuePreviousLeave && leaveEntity.continuationOf) {
        updateLeaveEntity("continuationOf", null);
        updateLeaveEntity("continuationOfId", null);
      }

      closeConvertModal(true);
    }
  };

  const renderToType = () => {
    switch (true) {
      case SICK_LEAVE_TYPES.includes(leaveEntity.type):
        return (
          <ToSickLeave
            leaveEntity={leaveEntity}
            updateLeaveEntity={updateLeaveEntity}
            inputErrors={inputErrors}
            setInputErrors={setInputErrors}
            disableDatePicker={!createNewAbsence}
          />
        );
      /* HIDDEN FOR NOW, (discussed in meeting 3.10.2024) AS EVERY OTHER TYPE IS USING GENERAL DETAILS UNTIL BE SUPPPOT IS ADDED
            case leaveEntity.type === "ANNUAL_HOLIDAY":
              return (
                <ToAnnualHoliday
                  leaveEntity={leaveEntity}
                  updateLeaveEntity={updateLeaveEntity}
                  inputErrors={inputErrors}
                  setInputErrors={setInputErrors}
                />
              );
              */
      default:
        return (
          <ToGeneralLeave
            leaveEntity={leaveEntity}
            setLeaveEntity={() => {}}
            entries={[]}
            setEntries={() => {}}
            updateLeaveEntity={updateLeaveEntity}
            inputErrors={inputErrors}
            setInputErrors={setInputErrors}
            showAttachments={false}
            disableDatePicker={!createNewAbsence}
          />
        );
    }
  };

  const absenceTypePlaceholder = t("hourEntries:absenceTypePlaceholder");

  return (
    <Box
      sx={{
        width: "572px",
        display: "flex",
        flexDirection: "column",
        borderTopRightRadius: "12px",
        borderTopLeftRadius: "12px",
      }}
    >
      <OverlaySpinner isLoading={isLoading} />
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
        }}
      >
        <Box>{t("hourEntries:absenceDescription")}</Box>
        <Select
          size="small"
          fullWidth
          displayEmpty
          value={leaveEntity.type || ""}
          onChange={(e) => updateLeaveEntity("type", e.target.value || "")}
          sx={{
            marginTop: "15px",
            backgroundColor: "rgba(249, 249, 250, 0.6)",
          }}
          renderValue={(absenceType) =>
            absenceType
              ? translatedEntryTypes[absenceType]
              : absenceTypePlaceholder
          }
        >
          {absenceTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.name}
            </MenuItem>
          ))}
        </Select>
        {renderToType()}
        <LeaveAccumulationInfo
          rows={accumulationRows}
          unit={accumulationUnit}
          labelKey={accumulationLabelKey}
          loading={accumulationLoading}
        />
        <Box
          className={DEFAULT_DRAG_HANDLE_SELECTOR}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            cursor: "grab",
          }}
        >
          <CustomButton
            onClick={() => handleAbort()}
            variant={"text"}
            sx={{
              minHeight: "42px",
              borderRadius: "20px",
              minWidth: "120px",
              marginTop: "35px",
            }}
          >
            {t("common:cancelShort")}
          </CustomButton>
          <CustomButton
            onClick={() => handleContinue()}
            disabled={!leaveEntity.type}
            variant={"contained"}
            sx={{
              minHeight: "42px",
              borderRadius: "20px",
              minWidth: "120px",
              marginTop: "35px",
              marginLeft: "10px",
            }}
          >
            {t("common:continue")}
          </CustomButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ConvertToAbsence;
